import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import classnames from 'classnames';
import Styled from 'styled';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import { ForumRectangle, ZoznamBoxin } from 'lib/google/dfp/ad-units/forum-rectangle';
import { Targetting } from 'lib/google/dfp/google-dfp';
import { InterscrollerPlaceholder } from 'lib/google/dfp/ad-units/interscroller';
import { ForumCategoryStrip } from 'lib/google/dfp/ad-units/forum-category-strip';

import message from 'four-nets/icons/svg/icon_comment.svg';
import users from 'four-nets/icons/svg/icon_users.svg';
import like from 'four-nets/icons/svg/icon_like.svg';
import PagerView from 'four-nets/ui/pager-view';
import SortDateView from 'four-nets/ui/sort-date-view';
import ThreadView, { ThreadViewModel } from 'four-nets/forum/category/thread-row';
import ThreadCategoryCatalog from 'four-nets/content-pairing/forum-vendors/thread-vendors-panel/thread-category-catalog/thread-category-catalog';

import PlaceholderThreadRow from './placeholder-thread-row';
import defaultStyles from './threads-table-view.scss';
import ScrollToTop from 'lib/scroll/scroll-to-top';

export class ThreadsTableViewModel {
    @observable threads;
    @observable threadViewModels;
    @observable loading = false;
    @observable ascending = false;

    setAscending(asc) {
        this.ascending = asc;
    }

    setThreads(threads) {
        if (threads) {
            this.threads = threads;

            this.threadViewModels = threads.results.map(thread => new ThreadViewModel({ thread }));
        }
    }

    toJSON() {
        return {
            ascending: this.ascending,
            threads: this.threads,
        };
    }
}

@Styled(defaultStyles)
@observer
class ThreadsTableView extends React.Component {
    static defaultProps = {
        sortable: true,
        category: null,
    };

    static contextTypes = {
        site: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = { squareAdVisible: false, stripAdVisible: false };
    }

    changeSort = (event, value) => {
        if (!this.props.sortable) {
            return;
        }

        const search = {
            ...queryString.parse(window.location.search),
            page: 1,
            asc: value,
        };

        this.props.history.push(`${window.location.pathname}?${queryString.stringify(search)}`);
    };

    slotRenderEndedCb = event => {
        this.setState({
            squareAdVisible: !event.isEmpty,
        });
    };

    stripSlotRenderEndedCb = event => {
        this.setState({
            stripAdVisible: !event.isEmpty,
        });
    };

    render() {
        const { styles, threadsTableViewModel, category, sortable, loading } = this.props;
        const threads = this.props.threadsTableViewModel?.threads;
        const threadViewModels = this.props.threadsTableViewModel?.threadViewModels;

        const { stripAdVisible, squareAdVisible } = this.state;
        const { site } = this.context;

        const usePlaceholderElements = (!threadViewModels && loading) || !threadViewModels;
        const rows = usePlaceholderElements ? Array(40).fill(null) : threadViewModels;

        return (
            <div className={styles.threadsTable}>
                <table className={styles.table}>
                    <thead className={styles.heading}>
                        <tr className={styles.headingRow}>
                            <th className={styles.headingLabel}>
                                <FormattedMessage id='THREAD_TITLE_COL' />
                            </th>
                            <th className={styles.headingIcon}>
                                <SVGIcon className={styles.icon} src={message} />
                            </th>
                            <th className={styles.headingIcon}>
                                <SVGIcon className={styles.icon} src={users} />
                            </th>
                            <th className={styles.headingIcon}>
                                <SVGIcon className={styles.icon} src={like} />
                            </th>
                            <th className={styles.headingSorting}>
                                <SortDateView
                                    sortable={sortable}
                                    ascending={threadsTableViewModel?.ascending}
                                    onClick={this.changeSort}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody className={styles.body}>
                        {rows.map((row, index) => {
                            const element = usePlaceholderElements ? (
                                <PlaceholderThreadRow key={`placeholder-${index}`} borderTop={index !== 0} />
                            ) : (
                                <ThreadView
                                    borderTop={index === 5 && stripAdVisible}
                                    threadViewModel={row}
                                    key={row.thread.id}
                                />
                            );

                            if (index === 8) {
                                const targettings = category
                                    ? [new Targetting('f-rectangle-cat-slug', [category.slug])]
                                    : [];

                                const classes = [styles.adLine];
                                if (!squareAdVisible) {
                                    classes.push(styles.adLineEmpty);
                                }
                                return (
                                    <React.Fragment key='rectangle-top-fragment'>
                                        <tr key='rectangle-top' className={classnames(classes)}>
                                            <td>
                                                {site.isWedding && site.isSlovak ? (
                                                    <ZoznamBoxin
                                                        position='top'
                                                        slotRenderEndedCb={this.slotRenderEndedCb}
                                                    />
                                                ) : (
                                                    <ForumRectangle
                                                        adUnitCode='forum-rectangle-top'
                                                        targettings={targettings}
                                                        slotRenderEndedCb={this.slotRenderEndedCb}
                                                    />
                                                )}
                                                <InterscrollerPlaceholder targettings={targettings} />
                                            </td>
                                        </tr>
                                        {element}
                                    </React.Fragment>
                                );
                            } else if (index === 4) {
                                const targettings = category
                                    ? [new Targetting('f-rectangle-cat-slug', [category.slug])]
                                    : [];

                                return (
                                    <React.Fragment key='pasik'>
                                        {element}

                                        {stripAdVisible ? (
                                            <tr className={styles.adLine}>
                                                <td>
                                                    <ForumCategoryStrip
                                                        targettings={targettings}
                                                        slotRenderEndedCb={this.stripSlotRenderEndedCb}
                                                    />
                                                </td>
                                            </tr>
                                        ) : (
                                            <ThreadCategoryCatalog threadCategoryId={category && category.id} />
                                        )}
                                    </React.Fragment>
                                );
                            }
                            return element;
                        })}
                    </tbody>
                </table>
                {threadViewModels?.length === 0 ? (
                    <div className={styles.noContent}>
                        <FormattedMessage id='FORUM_NO_THREADS' />.
                    </div>
                ) : null}
                {threads?.pages && threads.pages > 1 ? (
                    <div className={styles.pager}>
                        <PagerView count={threads.pages} current={threads.page} />
                        <ScrollToTop page={threads.page} scrollOnMount={false} />
                    </div>
                ) : null}
            </div>
        );
    }
}

export default withRouter(ThreadsTableView);
